<template>
  <v-dialog
    v-if="dialogOpen"
    :value="dialog"
    persistent
    :retain-focus="false"
    transition="fade-transition"
    fullscreen
    @keydown.esc="escape(closeDialog)"
  >
    <v-toolbar
      color="primaryBgr"
      dark
    >
      <p-btn-icon type="close" btn-class="mr-4" tabindex="3" @click="escape(closeDialog)"/>
      <v-toolbar-title>{{ dialogTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-toolbar-items>
        <v-btn text tabindex="2" :disabled="!isModelChanged()" @click="submit">{{ $t('save') }}</v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <v-card>
      <v-form ref="form" @submit.stop.prevent>
        <div class="p-dialog-full-screen-container">
          <v-row class="mt-0">
            <v-col cols="12" md="4" sm="6">
              <v-text-field
                type="text"
                v-model.trim="editedTemplate.name"
                :label="$t('scheduleTemplate.name')"
                :rules="rules.name"
                class="p-input-lg"
                tabindex="1"
                clearable
                autofocus
              ></v-text-field>
            </v-col>
              </v-row>
          <v-row>
            <v-col>
              <ejs-documenteditorcontainer
                v-if="ejsReady"
                ref="editor"
                :serviceUrl="editorServiceUrl"
                :documentName="editedTemplate.name"
                :enableToolbar="true"
                :toolbarItems="editorToolbarItems"
                height="600px"
                tabindex="1"
                @created="onEditorCreated"
                @toolbarClick="onEditorToolbarClick"
                @contentChange="onEditorContentChange"
              ></ejs-documenteditorcontainer>
            </v-col>
          </v-row>
        </div>
      </v-form>
    </v-card>

    <placeholder-dialog
      :dialog.sync="globalPlaceholderDialog"
      :keys="placeholdersScheduleGlobal"
      :title="$t('documentEditor.insertGlobalPlaceholder')"
      @insert="insertPlaceholder"
    ></placeholder-dialog>
  </v-dialog>
</template>


<script>
import Vue from 'vue'

import { mapActions, mapGetters } from 'vuex'

import PBtnIcon from '@/components/PBtnIcon'

import PlaceholderDialog from './ScheduleTemplatePlaceholderDialog'

import documentEditorMixin from '@/mixins/documentEditor.mixin'
import snackbarMixin from '@/mixins/snackbar.mixin'
import objectMixin from '@/mixins/object.mixin'
import dialogMixin from '@/mixins/dialog.mixin'
import validationMixin from '@/mixins/validation.mixin'
import unsavedChangesMixin from '@/mixins/unsavedChanges.mixin'

import { DocumentEditorContainerPlugin, Toolbar } from '@syncfusion/ej2-vue-documenteditor'

Vue.use(DocumentEditorContainerPlugin)


export default {
  provide: {
    DocumentEditorContainer: [Toolbar]
  },


  components: {
    PlaceholderDialog,
    PBtnIcon
  },


  mixins: [
    documentEditorMixin,
    snackbarMixin,
    objectMixin,
    dialogMixin,
    validationMixin,
    unsavedChangesMixin
  ],


  props: {
    template: Object
  },


  data () {
    return {
      globalPlaceholderDialog: false,
      editorServiceUrl: process.env.VUE_APP_DOCUMENT_EDITOR_API_URL,
      editedTemplate: {},
      defaultTemplate: {
        name: '',
        template: '',
        promoter: false
      },
      dialogOpen: false,
      ejsReady: false
    }
  },


  computed: {
    dialogTitle () {
      return this.template ? this.$t('scheduleTemplate.editTemplate') : this.$t('scheduleTemplate.createTemplate')
    },

    editorToolbarItems () {
      const docxItem = this.getToolbarItem(this.toolbarItemId.docx)
      const pdfItem = this.getToolbarItem(this.toolbarItemId.pdf)
      const globalPlaceholderItem = this.getToolbarItem(this.toolbarItemId.globalPlaceholder)

      return ['New', 'Open',
        'Separator', docxItem, pdfItem,
        'Separator', 'Undo', 'Redo',
        'Separator', globalPlaceholderItem, 'Image', 'Table', 'Hyperlink', 'TableOfContents',
        'Separator', 'Header', 'Footer', 'PageSetup', 'PageNumber', 'Break',
        'Separator', 'Find']
    },

    ...mapGetters('placeholder', ['placeholdersScheduleGlobal']),

    rulesToApply () {
      const rule = this.rule

      return {
        name: [
          rule.required()
        ]
      }
    }
  },


  methods: {
    ...mapActions('scheduleTemplate', ['createScheduleTemplate', 'updateScheduleTemplate']),
    ...mapActions('changes', ['removeFromChanges']),

    onDialogOpen () {
      if (this.template) {
        this.removeFromChanges(this.template)
      }
      this.prepareEditor()

      window.dispatchEvent(new Event('resize'))
      setTimeout(function () {
        window.dispatchEvent(new Event('resize'))
      }, 100)
      this.editedTemplate = this.getObjectCopy(this.template || this.defaultTemplate)
      this.initWatchedModel('editedTemplate')
      this.resetValidation()
    },

    async prepareEditor () {
      this.ejsReady = false
      await Vue.nextTick()
      this.dialogOpen = true
      await Vue.nextTick()
      this.ejsReady = true
      await Vue.nextTick()
      this.resetEditorSize()
      if (this.editorCreated) {
        this.setEditorContent()
      }
    },

    removeEditor () {
      this.ejsReady = false
    },

    updateTemplate () {
      this.editedTemplate.template = this.$refs.editor.ej2Instances.documentEditor.serialize()
    },

    onEditorContentChange () {
      this.updateTemplate()
    },

    onEditorCreated () {
      if (!this.$refs?.editor) return

      if (this.template) {
        this.$refs.editor.ej2Instances.documentEditor.open(this.template.template)
      } else {
        this.$refs.editor.ej2Instances.documentEditor.openBlank()
      }
      this.updateTemplate()
      this.initWatchedModel(this.editedTemplate)
    },

    async onEditorToolbarClick (args) {
      try {
        switch (args.item.id) {
          case this.toolbarItemId.globalPlaceholder:
            this.globalPlaceholderDialog = true
            break
          case this.toolbarItemId.pdf: {
            this.saveAsPdf(this.$refs.editor, this.editedTemplate.name || 'template')
            break
          }
          case this.toolbarItemId.docx: {
            this.saveAsDocx(this.$refs.editor, this.editedTemplate.name || 'template')
            break
          }
        }
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      }
    },

    insertPlaceholder (placeholder) {
      this.$refs.editor.ej2Instances.documentEditor.editor.insertText(placeholder)
    },

    async submit () {
      const valid = await this.validate(this.rulesToApply)
      if (!valid || !this.isModelChanged()) return

      try {
        this.showSnackbarProcessing()

        if (this.editedTemplate.id) {
          await this.updateScheduleTemplate(this.editedTemplate)
          this.showSnackbarSuccess(this.$t('scheduleTemplate.templateUpdated'))
        } else {
          await this.createScheduleTemplate(this.editedTemplate)
          this.showSnackbarSuccess(this.$t('scheduleTemplate.templateCreated'))
        }

        this.closeDialog()
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.removeEditor()
      }
    }
  }
}
</script>
